<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Users</h1>
      <app-cells class="cells--start-vertical">
        <app-button @click.native="onUserFormOpen">Create user</app-button>
        <app-input
          @input.native="onUsersSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by name, phone number, e-mail"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_groups"
          :options="groups_options"
          :reduce="item => item.code"
          :closeOnSelect="false"
          placeholder="Groups"
          multiple
          class="select--300"
        />
        <app-filters
          v-model="active_filter"
          :filters="active_filters"
          name="filterActive"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="super_filter"
          :filters="super_filters"
          name="filterSuper"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="fa_filter"
          :filters="fa_filters"
          name="filterFa"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__first_name>
          <div class="table-default__left">Name</div>
        </template>
        <template #h__username>
          <div class="table-default__left">Username</div>
        </template>
        <template #h__email>
          <div class="table-default__left">Email</div>
        </template>
        <template #h__groups>
          <div class="table-default__left">Roles</div>
        </template>
        <template #first_name="{ row }">
          <div class="table-default__left table-default__left--photo">
            <div class="table-default__photo">
              <img :src="getUserAvatar(row.crop.crop_url)" alt="Avatar">
            </div>
            <span>{{ row.full_name }}</span>
          </div>
        </template>
        <template #username="{ row }">
          <div class="table-default__left table-default__left--flex">
            <img v-if="row.is_2fa_enabled" class="table-default__2fa" src="@/assets/img/2fa-enabled-icon.svg" alt="">
            <img v-else class="table-default__2fa" src="@/assets/img/2fa-not-enabled-icon.svg" alt="">
            <button
                @click="onGetUserTokens(row.id, row.groups[0].index_page, row)"
                type="button"
                style="line-height: 0"
            >
              <img src="@/assets/img/log-in-icon.svg" alt="">
            </button>
            {{ row.username }}
          </div>
        </template>
        <template #email="{ row }">
          <div class="table-default__left">
            {{ row.email || '' }}
          </div>
        </template>
        <template #groups="{ row }">
          <template v-if="row.groups.length">
            <div class="table-default__left">
              <span>{{ row.groups.map(group => group.name).join(', ') }}</span>
            </div>
          </template>
          <template v-else>-</template>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onUserFormOpen(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="user-form"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('user-form')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ is_user_create ? 'Create' : 'Update' }} user</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="First name" label-for="first_name" required>
                  <app-input
                    v-model="form.first_name"
                    @change.native="$v.form.first_name.$touch()"
                    :error="$v.form.first_name.$error"
                    id="first_name"
                    placeholder="First name"
                    name="first_name"
                  />
                  <template #error>
                    <div v-if="$v.form.first_name.$dirty && !$v.form.first_name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Last name" label-for="last_name">
                  <app-input
                    v-model="form.last_name"
                    id="last_name"
                    placeholder="Last name"
                    name="last_name"
                  />
                </app-form-group>
                <app-form-group label="Photo">
                  <app-cropper-web-cam
                    v-model="form.crop"
                    ref="cropper"
                    :aspectRatio="1/1"
                    class="cropper--square"
                  />
                </app-form-group>
                <app-form-group label="E-mail" label-for="email">
                  <app-input
                    v-model="form.email"
                    @change.native="$v.form.email.$touch"
                    :error="$v.form.email.$error"
                    id="email"
                    type="email"
                    placeholder="E-mail"
                    name="email"
                  />
                  <template #error>
                    <div v-if="$v.form.email.$dirty && !$v.form.email.email">Enter the correct e-mail format</div>
                  </template>
                </app-form-group>
                <app-form-group label="Employee">
                  <app-select
                    @search="onEmployeeSearch"
                    v-model="form.employee"
                    :options="employee_options"
                    label="full_name"
                    placeholder="Employee"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Phone">
                  <app-phone
                    v-model="form.phone"
                    placeholder="Phone number"
                  />
                </app-form-group>
                <app-form-group label="Messengers">
                  <app-cells>
                    <app-checkbox v-model="form.phone_telegram" label="Telegram" />
                    <app-checkbox v-model="form.phone_whatsapp" label="WhatsApp" />
                  </app-cells>
                </app-form-group>
                <app-form-group label="Telegram ID" label-for="telegram_id">
                  <app-input
                    v-model="form.telegram_id"
                    id="telegram_id"
                    placeholder="Telegram ID"
                    name="telegram_id"
                  />
                </app-form-group>
                <app-form-group label="Username" label-for="username" required>
                  <app-input
                    v-model="form.username"
                    @change.native="$v.form.username.$touch()"
                    :error="$v.form.username.$error"
                    id="username"
                    placeholder="Username"
                    name="username"
                  />
                  <template #error>
                    <div v-if="$v.form.username.$dirty && !$v.form.username.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label-for="password" label="Password" required>
                  <template #additional>
                    <button
                      @click="onPasswordGen"
                      type="button"
                      class="form-group__additional-btn"
                    >
                      <img src="@/assets/img/generate-icon.svg" alt="Generate">
                    </button>
                  </template>
                  <app-input
                    v-model="form.password"
                    @change.native="$v.form.password.$touch"
                    :error="$v.form.password.$error"
                    type="text"
                    id="password"
                    placeholder="Password"
                    name="password"
                  />
                  <template #error>
                    <div v-if=" $v.form.password.$dirty && !$v.form.password.minLength">
                      Password length less than 4 characters
                    </div>
                    <div v-if="$v.form.password.$dirty && !$v.form.password.enAlpha">
                      Only letters of the English alphabet and numbers are accepted
                    </div>
                    <div v-if="$v.form.password.$dirty && !$v.form.password.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Groups" required>
                  <app-select
                    @input="onGroupsChange"
                    v-model="form.groups"
                    :options="groups_options"
                    :closeOnSelect="false"
                    multiple
                    placeholder="Groups"
                    :error="$v.form.groups.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.groups.$dirty && !$v.form.groups.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="sales_manager_in_groups" label="Lead languages" required>
                  <app-select
                    v-model="form.leads_languages"
                    :options="languages_options"
                    :closeOnSelect="false"
                    multiple
                    placeholder="Lead languages"
                    :error="$v.form.leads_languages.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.leads_languages.$dirty && !$v.form.leads_languages.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="hotel_reception_in_groups" label="Hotel property" required>
                  <app-select
                    v-model="form.hotel_property"
                    :options="hotel_property_options"
                    placeholder="Hotel poperty"
                    :error="$v.form.hotel_property.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.hotel_property.$dirty && !$v.form.hotel_property.required">Required field</div>
                  </template>
                </app-form-group>
                <app-cells :indent="false" v-if="!is_user_create">
                  <app-form-group label="Active">
                    <app-switcher v-model="form.is_active" />
                  </app-form-group>
                  <app-form-group label="2FA">
                    <app-switcher v-model="form.is_2fa_enabled" />
                  </app-form-group>
                </app-cells>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">{{ is_user_create ? 'Create' : 'Save' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getEmployeesListSearch,
  getHotelManagementProperties,
  getLanguages,
  getUsers,
  getUsersGroups,
  getUserTokens,
  patchUsers,
  postUsers,
} from '@/http'
import debounce from 'lodash/debounce'
import { getAccessToken, getRefreshToken, setAuthTokens } from 'axios-jwt'
import { email, helpers, minLength, required, requiredIf } from 'vuelidate/lib/validators'

const enAlpha = helpers.regex('ruAlpha', /^[a-zA-Z0-9\s]*$/)

export default {
  name: 'Users',
  data() {
    return {
      is_loaded: false,
      form: {},
      is_user_create: false,
      search: null,
      table: {
        items: [],
        columns: ['first_name', 'username', 'email', 'groups', 'controls'],
        options: {
          headings: {
            first_name: 'Name',
            username: 'Username',
            email: 'Email',
            groups: 'Roles',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      active_filter: 'true',
      active_filters: [
        { value: 'true', label: 'Active' },
        { value: '', label: 'Not active' },
      ],
      super_filter: '',
      super_filters: [
        { value: '', label: 'Regular' },
        { value: 'true', label: 'Super' },
      ],
      fa_filter: '',
      fa_filters: [
        { value: '', label: 'All' },
        { value: 'false', label: 'No 2FA' },
        { value: 'true', label: '2FA' },
      ],
      groups_options: [],
      employee_options: [],
      languages_options: [],
      hotel_property_options: [],
      filter_groups: null,
      table_preloader: false
    }
  },
  validations: {
    form: {
      first_name: { required },
      email: { email },
      username: { required },
      password: { required: requiredIf(function () { return this.is_user_create }), minLength: minLength(4), enAlpha },
      groups: { required },
      leads_languages: { required: requiredIf(function () { return this.sales_manager_in_groups }) },
      hotel_property: { required: requiredIf(function () { return this.hotel_reception_in_groups }) },
    }
  },
  computed: {
    sales_manager_in_groups() {
      if (!this.form.groups?.length) return
      return this.form.groups.some(item => item.code === 'sales_manager')
    },
    hotel_reception_in_groups() {
      if (!this.form.groups?.length) return
      return this.form.groups.some(item => item.code === 'hotel_reception')
    }
  },
  created() {
    this.fetchItems()
    getUsersGroups().then(res => { this.groups_options = res.data })
    getLanguages().then(res => { this.languages_options = res.data })
    getHotelManagementProperties().then(res => { this.hotel_property_options = res.data })
  },
  methods: {
    onGetUserTokens(id, index_page, user_data) {
      getUserTokens(id).then(res => {
        localStorage.setItem('user_groups_admin', localStorage.getItem('userGroups'))
        localStorage.setItem('access_admin', getAccessToken())
        localStorage.setItem('refresh_admin', getRefreshToken())
        this.$store.commit('permissions/reset')
        setAuthTokens({
          accessToken: res.data.access,
          refreshToken: res.data.refresh
        })
        this.$store.commit('setUserGroups', user_data.groups)
        this.$router.push({ name: index_page })
        setTimeout(() => {
          location.reload()
        }, 500)
      })
    },
    onGroupsChange() {
      if (!this.sales_manager_in_groups) this.form.leads_languages = []
      if (!this.hotel_reception_in_groups) this.form.hotel_property = null
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.search = ''
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    fetchItems() {
      getUsers({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        is_active: this.search ? '' : !!this.active_filter,
        is_superuser: this.search ? '' : !!this.super_filter,
        is_2fa_enabled: this.search ? '' : this.fa_filter === '' ? '' : this.fa_filter !== 'false',
        groups: this.filter_groups,
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onCheckForm() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        if (this.form.crop?.coords && !this.form.crop.photo) {
          this.$refs.submit.preload = true
          this.$refs.cropper.sendCrop()
            .then(() => { this.sendForm() })
            .catch(() => { this.$refs.submit.preload = false })
        } else {
          this.sendForm()
        }
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.is_user_create) {
        postUsers(this.normalizeForm())
          .finally(() => { this.$refs.submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'User created',
            })
            this.$modal.hide('user-form')
            this.fetchItems()
          })
      } else {
        if (this.form.password === '') delete this.form.password
        if (this.form.is_2fa_enabled) delete this.form.is_2fa_enabled
        patchUsers(this.form.id, this.normalizeForm())
          .finally(() => { this.$refs.submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'User updated',
            })
            this.$modal.hide('user-form')
            this.fetchItems()
          })
      }
      this.$v.form.$reset()
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      if (normalizedForm.crop?.photo) {
        normalizedForm.crop = { ...normalizedForm.crop }
        delete normalizedForm.crop.crop_url
        delete normalizedForm.crop.photo_url
      } else if (normalizedForm.crop?.photo === null) {
        normalizedForm.crop = { photo: null }
      } else {
        delete normalizedForm.crop
      }

      normalizedForm.groups = normalizedForm.groups.map(item => item.id)
      if (this.sales_manager_in_groups) normalizedForm.leads_languages = normalizedForm.leads_languages.map(item => item.code)
      else normalizedForm.leads_languages = []
      if (!normalizedForm.last_name) normalizedForm.last_name = null
      if (normalizedForm.employee) normalizedForm.employee = normalizedForm.employee.id
      if (normalizedForm.hotel_property) normalizedForm.hotel_property = normalizedForm.hotel_property.code

      return normalizedForm
    },
    onUsersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onUserFormOpen(user) {
      if (user.first_name) {
        this.form = { ...user, password: '' }
        this.is_user_create = false
      } else {
        this.form = { password: '', crop: {}, leads_languages: [] }
        this.is_user_create = true
      }
      this.$modal.show('user-form')
    },
    onPasswordGen() {
      this.form.password = ''
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
      for (let i = 0, n = charset.length; i < 8; i++) {
        this.form.password += charset.charAt(Math.floor(Math.random() * n))
      }
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employee_options = []
      } else {
        getEmployeesListSearch({search, employment_status: 'working'}).then(response => {
          vm.employee_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
